import React, { useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { LDProvider } from "launchdarkly-react-client-sdk";
import config from "../config";
import { useUserMsp } from "../hooks/useUserMsp";

interface LaunchDarklyProviderProps {
  children: React.ReactNode;
}

type LaunchDarklyContext = {
  kind: "user";
  key: string;
  anonymous?: boolean;
  email?: string;
  name?: string;
  mspId?: string;
  mspName?: string;
  appHost?: string;
};

const LaunchDarklyProvider: React.FC<LaunchDarklyProviderProps> = ({ children }) => {
  const { user, isLoading } = useAuth0();
  const { userMspInfo } = useUserMsp();
  const normalizedMspName = userMspInfo?.msp_name?.trim();

  const context = useMemo<LaunchDarklyContext>(() => {
    if (!user || isLoading) {
      return {
        kind: "user",
        key: "anonymous",
        anonymous: true,
      };
    }

    return {
      kind: "user",
      key: user.email || user.sub || "unknown",
      email: user.email,
      name: user.name,
      mspId: userMspInfo?.msp_id ? String(userMspInfo.msp_id) : undefined,
      mspName: normalizedMspName || undefined,
      appHost: window.location.hostname,
    };
  }, [user, isLoading, normalizedMspName, userMspInfo?.msp_id]);

  useEffect(() => {
    // Debug signal to validate which context attributes LaunchDarkly receives.
    console.info("[LaunchDarkly] initializing context", {
      key: context.key,
      email: context.anonymous ? undefined : context.email,
      mspId: context.anonymous ? undefined : context.mspId,
      mspName: context.anonymous ? undefined : context.mspName,
      appHost: context.anonymous ? undefined : context.appHost,
      anonymous: Boolean(context.anonymous),
    });
  }, [context]);

  const ldProviderKey = useMemo(() => {
    const keyPart = String(context.key ?? "unknown");
    const mspPart = String(context.mspName ?? "no-msp");
    return `${keyPart}:${mspPart}`;
  }, [context]);

  // One-time secret page is public and has no flag dependency. Skip LD entirely
  // so Auth0/MSP identity changes cannot remount the page mid-view.
  const isPublicPasswordRoute =
    typeof window !== "undefined" && window.location.pathname === "/password";

  if (!config.launchdarklyClientSideId || isPublicPasswordRoute) {
    return <>{children}</>;
  }

  return (
    <LDProvider
      key={ldProviderKey}
      clientSideID={config.launchdarklyClientSideId}
      context={context}
      timeout={5}
      reactOptions={{ useCamelCaseFlagKeys: false }}
    >
      {children}
    </LDProvider>
  );
};

export default LaunchDarklyProvider;
